import React, { useEffect, useRef, useState } from "react";
import { updateProfile, changePassword, setToken, openBillingPortal, fetchAiCredit } from "../api";
import UpgradePanel from "./UpgradePanel";
import { ageFromDob, todayIso } from "../utils/age";

export default function UserProfileMenu({ currentUser, onLogout, onUserUpdate }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // "menu" | "profile" | "password" | "subscription"
  const menuRef = useRef(null);

  const displayName = currentUser?.display_name || currentUser?.email || "User";
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setView("menu");
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const close = () => { setOpen(false); setView("menu"); };

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar button */}
      <button
        onClick={() => { setOpen((v) => !v); setView("menu"); }}
        className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
        aria-label="User menu"
      >
        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-ink-900 text-xs font-bold shrink-0 select-none">
          {initials || "?"}
        </span>
        <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[100px] truncate">
          {displayName}
        </span>
        <svg className="w-4 h-4 text-slate-400 hidden sm:block" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 z-50 overflow-hidden">
          {view === "menu" && (
            <MenuView
              displayName={displayName}
              email={currentUser?.email}
              initials={initials}
              hasSubscription={!!currentUser?.subscription}
              onProfile={() => setView("profile")}
              onPassword={() => setView("password")}
              onSubscription={() => setView("subscription")}
              onLogout={() => { close(); onLogout(); }}
            />
          )}
          {view === "subscription" && (
            <SubscriptionView
              subscription={currentUser?.subscription}
              onBack={() => setView("menu")}
            />
          )}
          {view === "profile" && (
            <ProfileView
              currentUser={currentUser}
              onSave={(updated) => { onUserUpdate(updated); close(); }}
              onBack={() => setView("menu")}
            />
          )}
          {view === "password" && (
            <PasswordView
              onSave={(token) => { setToken(token); close(); }}
              onBack={() => setView("menu")}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Monthly AI-usage meter (percentage only — internal dollar figures stay
 * private). Fetches when the dropdown opens; renders nothing unless metering
 * is in soft/hard mode, so it's invisible during the observe/shadow stages.
 */
function AiUsageMeter() {
  const [credit, setCredit] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAiCredit()
      .then((data) => { if (!cancelled) setCredit(data); })
      .catch(() => {}); // best-effort; the menu works without it
    return () => { cancelled = true; };
  }, []);

  if (!credit || !credit.active || credit.percent_used == null) return null;

  const pct = credit.percent_used;
  const barColor = pct >= 95 ? "bg-rose-500" : pct >= 75 ? "bg-amber-500" : "bg-gold-500";
  const resetDate = credit.resets_at
    ? new Date(credit.resets_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : null;

  return (
    <div className="px-4 py-3 border-b border-slate-100">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
        <span>AI usage this month</span>
        <span className="font-medium text-slate-700">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      {resetDate && (
        <p className="mt-1.5 text-[11px] text-slate-400">Resets {resetDate}</p>
      )}
    </div>
  );
}

function MenuView({ displayName, email, initials, hasSubscription, onProfile, onPassword, onSubscription, onLogout }) {
  return (
    <>
      {/* User info header */}
      <div className="px-4 py-4 border-b border-slate-100 flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 flex items-center justify-center text-ink-900 text-sm font-bold shrink-0">
          {initials || "?"}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
          <p className="text-xs text-slate-500 truncate">{email}</p>
        </div>
      </div>

      {/* Monthly AI usage (soft/hard metering modes only) */}
      <AiUsageMeter />

      {/* Menu items */}
      <div className="py-2">
        <button
          onClick={onProfile}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <svg className="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Edit Profile
        </button>
        <button
          onClick={onPassword}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <svg className="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          Change Password
        </button>
        {hasSubscription && (
          <button
            onClick={onSubscription}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            Subscription
          </button>
        )}
      </div>

      {/* Sign out */}
      <div className="border-t border-slate-100 py-2">
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--tag-rose)] hover:bg-[var(--tag-rose-bg)] transition-colors"
        >
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Sign out
        </button>
      </div>
    </>
  );
}

function SubscriptionView({ subscription, onBack }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const status = subscription?.status || "";
  const cancelAtEnd = !!subscription?.cancel_at_period_end;
  const periodEnd = subscription?.current_period_end;
  const trialEnd = subscription?.trial_end;

  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d) ? null : d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  };

  // Human-readable summary of the current state.
  let label;
  let detail;
  // Our own trial: no Stripe subscription behind it (see /api/auth/me/).
  const appTrial = status === "trialing" && subscription?.stripe === false;

  if (status === "trialing") {
    label = "Free trial";
    detail = cancelAtEnd
      ? `Ends on ${fmt(trialEnd) || "the trial end date"} — won't renew.`
      : `Your trial ${trialEnd ? `ends on ${fmt(trialEnd)}` : "is active"}.`;
  } else if (status === "active") {
    label = "Active";
    detail = cancelAtEnd
      ? `Cancels on ${fmt(periodEnd) || "the period end"} — you'll keep access until then.`
      : periodEnd ? `Renews on ${fmt(periodEnd)}.` : "Your subscription is active.";
  } else if (status === "past_due" || status === "unpaid") {
    label = "Payment due";
    detail = "There's a problem with your latest payment. Update your card to keep access.";
  } else if (status === "canceled") {
    label = "Canceled";
    detail = "Your subscription has ended.";
  } else {
    label = status || "—";
    detail = "Manage your subscription and billing in the Stripe portal.";
  }

  const openPortal = async () => {
    setError(null);
    setLoading(true);
    try {
      const { url } = await openBillingPortal();
      window.location.assign(url);
    } catch (e) {
      setError(e.message || "Could not open the billing portal.");
      setLoading(false);
    }
  };

  return (
    <div className="p-4">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4 transition-colors">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Subscription</h3>

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-800">Status</span>
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--tag-teal)] bg-[var(--tag-teal-bg)] border border-[var(--panel-line)] rounded-full px-2 py-0.5">{label}</span>
        </div>
        {detail && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{detail}</p>}
      </div>

      {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

      {appTrial ? (
        // No Stripe customer exists during our own trial, so the billing portal
        // would open empty. Offer the thing they'd actually want instead.
        <UpgradePanel note="Subscribing now keeps your remaining free days — billing starts when the trial would have ended." />
      ) : (
        <>
          <button
            onClick={openPortal}
            disabled={loading}
            className="w-full py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter] disabled:opacity-50"
          >
            {loading ? "Opening…" : "Manage or cancel subscription"}
          </button>
          <p className="text-xs text-slate-400 mt-2 text-center">
            Opens Stripe's secure billing portal, where you can cancel, change your card, or view invoices.
          </p>
        </>
      )}
    </div>
  );
}

function ProfileView({ currentUser, onSave, onBack }) {
  const LEGACY_COUNTRY_MAP = { UK: "GB", "United Kingdom": "GB", USA: "US", "United States": "US", UAE: "AE", "United Arab Emirates": "AE" };
  const normaliseCountry = (v) => LEGACY_COUNTRY_MAP[v?.trim()] || v?.trim() || "";
  const [country, setCountry] = useState(normaliseCountry(currentUser?.country));
  const dobAlreadySet = !!currentUser?.date_of_birth;
  const [dob, setDob] = useState(currentUser?.date_of_birth || "");
  const [goal, setGoal] = useState(
    currentUser?.daily_goal_minutes ? String(currentUser.daily_goal_minutes) : ""
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!dobAlreadySet && dob && ageFromDob(dob) < 13) {
      setError("You must be at least 13 years old.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = { country, daily_goal_minutes: goal ? Number(goal) : null };
      // DOB is set-once; only send it if the user is supplying it for the first time.
      if (!dobAlreadySet && dob) payload.date_of_birth = dob;
      const updated = await updateProfile(payload);
      onSave(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4 transition-colors">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Edit Profile</h3>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">First Name</label>
            <div className="w-full px-3 py-2 rounded-lg border border-slate-100 bg-slate-50 text-sm text-slate-500 cursor-not-allowed">
              {currentUser?.first_name || "—"}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Last Name</label>
            <div className="w-full px-3 py-2 rounded-lg border border-slate-100 bg-slate-50 text-sm text-slate-500 cursor-not-allowed">
              {currentUser?.last_name || "—"}
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-400">Name cannot be changed after signup.</p>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Country</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)] bg-white"
          >
            <option value="">— Select country —</option>
            <optgroup label="Arab World">
              <option value="SA">🇸🇦 Saudi Arabia</option>
              <option value="AE">🇦🇪 UAE</option>
              <option value="EG">🇪🇬 Egypt</option>
              <option value="JO">🇯🇴 Jordan</option>
              <option value="LB">🇱🇧 Lebanon</option>
              <option value="SY">🇸🇾 Syria</option>
              <option value="IQ">🇮🇶 Iraq</option>
              <option value="YE">🇾🇪 Yemen</option>
              <option value="MA">🇲🇦 Morocco</option>
              <option value="DZ">🇩🇿 Algeria</option>
              <option value="TN">🇹🇳 Tunisia</option>
              <option value="LY">🇱🇾 Libya</option>
              <option value="PS">🇵🇸 Palestine</option>
              <option value="BH">🇧🇭 Bahrain</option>
              <option value="KW">🇰🇼 Kuwait</option>
              <option value="OM">🇴🇲 Oman</option>
              <option value="QA">🇶🇦 Qatar</option>
              <option value="SD">🇸🇩 Sudan</option>
              <option value="MR">🇲🇷 Mauritania</option>
              <option value="SO">🇸🇴 Somalia</option>
              <option value="KM">🇰🇲 Comoros</option>
              <option value="DJ">🇩🇯 Djibouti</option>
            </optgroup>
            <optgroup label="Rest of the World">
              <option value="GB">🇬🇧 United Kingdom</option>
              <option value="US">🇺🇸 United States</option>
              <option value="CA">🇨🇦 Canada</option>
              <option value="AU">🇦🇺 Australia</option>
              <option value="DE">🇩🇪 Germany</option>
              <option value="FR">🇫🇷 France</option>
              <option value="NL">🇳🇱 Netherlands</option>
              <option value="SE">🇸🇪 Sweden</option>
              <option value="NO">🇳🇴 Norway</option>
              <option value="DK">🇩🇰 Denmark</option>
              <option value="PK">🇵🇰 Pakistan</option>
              <option value="IN">🇮🇳 India</option>
              <option value="TR">🇹🇷 Turkey</option>
              <option value="ID">🇮🇩 Indonesia</option>
              <option value="MY">🇲🇾 Malaysia</option>
              <option value="NG">🇳🇬 Nigeria</option>
              <option value="ZA">🇿🇦 South Africa</option>
            </optgroup>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Daily goal</label>
          <select
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)] bg-white"
          >
            <option value="">No daily reminder</option>
            <option value="5">5 minutes a day</option>
            <option value="10">10 minutes a day</option>
            <option value="15">15 minutes a day</option>
            <option value="30">30 minutes a day</option>
          </select>
          <p className="text-xs text-slate-400 mt-1">
            A gentle once-a-day reminder — you learn at your own pace.
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Date of Birth</label>
          {dobAlreadySet ? (
            <div className="w-full px-3 py-2 rounded-lg border border-slate-100 bg-slate-50 text-sm text-slate-500 cursor-not-allowed">
              {currentUser.date_of_birth}
            </div>
          ) : (
            <>
              <input
                type="date"
                value={dob}
                max={todayIso()}
                onChange={(e) => setDob(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)] bg-white"
              />
              <p className="text-xs text-slate-400 mt-1">
                Required for age-restricted features such as practice calls. Cannot be changed once set.
              </p>
            </>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
          <input
            type="email"
            value={currentUser?.email || ""}
            disabled
            className="w-full px-3 py-2 rounded-lg border border-slate-100 bg-slate-50 text-base text-slate-400 cursor-not-allowed"
          />
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function PasswordView({ onSave, onBack }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    if (!current || !next) { setError("Please fill in all fields."); return; }
    if (next !== confirm) { setError("New passwords don't match."); return; }
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    setSaving(true);
    setError(null);
    try {
      const { token } = await changePassword(current, next);
      onSave(token);
      setSuccess(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="p-6 text-center">
        <div className="text-3xl mb-2">✅</div>
        <p className="text-sm font-semibold text-slate-800">Password changed!</p>
        <p className="text-xs text-slate-500 mt-1">You are still logged in with your new password.</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4 transition-colors">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Change Password</h3>
      <div className="space-y-3">
        {[
          { label: "Current Password", value: current, onChange: setCurrent },
          { label: "New Password", value: next, onChange: setNext },
          { label: "Confirm New Password", value: confirm, onChange: setConfirm },
        ].map(({ label, value, onChange }) => (
          <div key={label}>
            <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
            <input
              type="password"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            />
          </div>
        ))}
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-2 rounded-lg bg-dt-gold-bright text-dt-ink text-sm font-semibold hover:brightness-105 transition-[filter] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Change Password"}
        </button>
      </div>
    </div>
  );
}
